import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, makeStyles, Paper, Typography } from "@material-ui/core";
import AddScopeModal from "./AddScopeModal";

interface Props {
  scopes: string[];
  onAddScope: (scope: string) => Promise<void>;
}

const INITIAL_MAX_ITEMS = 12;

export default function MxHeroAI(props: Props) {
  const { t } = useTranslation("storage");
  const { scopes, onAddScope } = props;
  const classes = useStyles();
  const [openModal, setOpenModal] = useState(false);
  const [scopesToShow, setScopesToShow] = useState(INITIAL_MAX_ITEMS);
  const showVisibilityBtn = scopes.length > INITIAL_MAX_ITEMS;
  const hiddenItemsCount = scopes.length - INITIAL_MAX_ITEMS;

  const handleVisibility = () => {
    setScopesToShow(prev => {
      if (scopes.length === prev) return INITIAL_MAX_ITEMS;
      return scopes.length;
    });
  };

  return (
    <Paper style={{ padding: 16 }}>
      <div className={classes.sectionTitle}>
        <Typography variant="subtitle2">{t("mxheroAI.scopesTitle")}</Typography>
        <Typography
          className={classes.subtitle}
          variant="caption"
          color="textSecondary"
        >
          {t("mxheroAI.scopesDescription")}
        </Typography>
      </div>
      <div className={classes.scopesWrapper}>
        <div className={classes.scopesContainer}>
          {scopes.slice(0, scopesToShow).map((s, i) => (
            <>
              <div className={classes.scopeItem}>{s}</div>
              {showHiddenCounter(scopes.length, scopesToShow, i) && (
                <Typography
                  variant="body2"
                  color="textSecondary"
                  style={{ fontWeight: 500, marginLeft: 10 }}
                >
                  + {hiddenItemsCount}
                </Typography>
              )}
            </>
          ))}
        </div>
        <div className={classes.buttonsWrapper}>
          <Button
            size="small"
            variant="contained"
            disableElevation
            color="primary"
            onClick={() => setOpenModal(true)}
          >
            {t("mxheroAI.addScopeBtn")}
          </Button>
          {showVisibilityBtn && (
            <Button variant="outlined" size="small" onClick={handleVisibility}>
              {t(
                scopes.length !== scopesToShow
                  ? "mxheroAI.showAllBtn"
                  : "mxheroAI.collapseBtn"
              )}
            </Button>
          )}
        </div>
      </div>
      {openModal && (
        <AddScopeModal
          isOpen={openModal}
          onClose={() => setOpenModal(false)}
          onAddScope={onAddScope}
        />
      )}
    </Paper>
  );
}

function showHiddenCounter(
  totalItems: number,
  scopesToShow: number,
  index: number
) {
  return scopesToShow - 1 === index && totalItems > scopesToShow;
}

const useStyles = makeStyles({
  sectionTitle: {
    display: "flex",
    flexDirection: "column",
  },
  subtitle: { maxWidth: "70%" },
  scopesWrapper: { display: "flex", alignItems: "center", gap: 20 },
  scopesContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    padding: "25px 16px",
    gap: 5,
    marginTop: 15,
    backgroundColor: "rgb(238, 242, 246)",
    border: "2px dashed rgba(0, 0, 0, 0.30)",
    width: "70%",
    borderRadius: 10,
    flexWrap: "wrap",
  },
  scopeItem: {
    padding: "16px 28px",
    border: "1px solid #009ed2",
    background: "#00aadf",
    color: "white",
    fontWeight: 500,
    borderRadius: 8,
    maxHeight: "fit-content",
  },
  buttonsWrapper: {
    display: "flex",
    flexDirection: "column",
    gap: 10,
  },
});
